import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FiFilter } from 'react-icons/fi';
import { db } from '../utils/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductCard from '../components/ProductCard';
import { useScrollReveal } from '../hooks/useGsapMotion';

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'computer-accessories', label: 'Computer Accessories' },
  { value: 'audio-sound', label: 'Audio & Sound' },
  { value: 'smart-devices', label: 'Smart Devices' },
  { value: 'power-storage', label: 'Power & Storage' },
  { value: 'home-appliances', label: 'Home Appliances' },
  { value: 'gaming', label: 'Gaming' }
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const pageRef = useRef(null);

  useScrollReveal(pageRef, [loading, products.length, sortBy, filterBy]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productQuery =
          filterBy === 'all'
            ? query(collection(db, 'products'))
            : query(collection(db, 'products'), where('category', '==', filterBy));

        const snapshot = await getDocs(productQuery);
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filterBy]);

  const sortedProducts = useMemo(() => {
    const data = [...products];
    data.sort((a, b) => {
      const field = getSortField(sortBy);
      const direction = getSortDirection(sortBy);

      let aValue = a[field] || '';
      let bValue = b[field] || '';

      if (field === 'price' || field === 'rating') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (field === 'createdAt') {
        aValue = aValue?.toDate?.() || new Date(0);
        bValue = bValue?.toDate?.() || new Date(0);
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      return direction === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });
    return data;
  }, [products, sortBy]);

  if (loading) return <LoadingSpinner />;

  return (
    <div ref={pageRef} className="sc-container py-8 md:py-10">
      <section className="sc-soft-card mb-7 p-6" data-reveal>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Product Discovery</p>
        <h1 className="text-3xl font-black text-[#1A1A1A] md:text-4xl">All Products</h1>
        <p className="mt-2 text-sm text-slate-500">
          Showing {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
          {filterBy !== 'all' && ` in ${categories.find((c) => c.value === filterBy)?.label}`}
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              Category
            </span>
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="sc-input"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              Sort By
            </span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sc-input">
              <option value="name">Name (A-Z)</option>
              <option value="price-low">Price (Low to High)</option>
              <option value="price-high">Price (High to Low)</option>
              <option value="rating">Rating (High to Low)</option>
              <option value="newest">Newest First</option>
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500">
            <FiFilter size={12} />
            Active filter
          </span>
          <span className="sc-chip">{categories.find((c) => c.value === filterBy)?.label}</span>
        </div>
      </section>

      {sortedProducts.length === 0 ? (
        <div className="sc-soft-card p-12 text-center" data-reveal>
          <h3 className="text-xl font-bold text-slate-700">No products found</h3>
          <p className="mt-2 text-sm text-slate-500">Try selecting another category or clearing filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4" data-stagger>
          {sortedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

const getSortField = (sortBy) => {
  switch (sortBy) {
    case 'price-low':
    case 'price-high':
      return 'price';
    case 'rating':
      return 'rating';
    case 'newest':
      return 'createdAt';
    default:
      return 'name';
  }
};

const getSortDirection = (sortBy) => {
  switch (sortBy) {
    case 'price-high':
    case 'rating':
    case 'newest':
      return 'desc';
    default:
      return 'asc';
  }
};

export default Products;
